import { ICustomerPetListPet } from "@/api_framework/api_modals/Pets";
import RBACFlag from "@/app/components/core/RBACFlag";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import PetDetailsLoader from "@/app/components/ui/Loader/PetDetailsLoader";
import PetBreed from "@/app/components/ui/Reusable/PetBreed";
import PetName from "@/app/components/ui/Reusable/PetName";
import PetSpecies from "@/app/components/ui/Reusable/PetSpecies";
import PetVaccinationStatus from "@/app/components/ui/Reusable/PetVaccinationStatus";
import { useLoader } from "@/app/hooks/useLoader";
import { useUtils } from "@/app/hooks/useUtils";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { FaUserPlus } from "react-icons/fa6";
import { ImPencil } from "react-icons/im";
import { IoRefresh } from "react-icons/io5";
import { useLocation } from "react-router";


const labelClass = "text-xs text-[#808DA0] font-medium";
const valueClass = "text-xs text-black";
const cellClass = "min-w-0 rounded-md p-0 sm:p-2";

const PetInformationDefaultView: React.FC<{
  petDetails: ICustomerPetListPet;
  handleEditPet: () => void;
  handleAddPet: () => void;
}> = ({ petDetails, handleEditPet, handleAddPet }) => {
  const isPetFetchInProgress = useLoader("isPetListFetchInProgress");
  const isCustomerDetailsFetchInProgress = useLoader("isCustomerDetailsFetchInProgress");
  const { dateToAge } = useUtils();
  const { pathname } = useLocation();
  const isOrdersRoute = pathname?.includes("/orders");

  const handleRefresh = () => {
    window.location.reload();
  };

  if (isPetFetchInProgress || isCustomerDetailsFetchInProgress) {
    return <PetDetailsLoader />;
  }

  return (
    <>
      {
        petDetails ? (
          <div className="flex w-full flex-row flex-wrap gap-3 sm:gap-2 md:gap-6 xl:gap-7 p-2" >
            <div className="flex flex-wrap items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1  font-medium ">
                  <PetName className="text-xs" petName={petDetails?.name} petStatus={petDetails?.status} />
                  <span className="mt-0.5 flex items-center text-xs font-normal text-[#808da0] ">
                    ({petDetails?.pet_code})
                    <ClipboardComponent
                      className="ml-1"
                      tooltipText={`Copy ${petDetails?.pet_code}`}
                      text={`${petDetails?.pet_code}`}
                      copyId={`copy-pet-code:${petDetails?.id}`}
                    />
                  </span>
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {petDetails?.dob === null ? (
                    <span className="text-xs font-normal text-[#ff0000]">Age !,</span>
                  ) : (
                    <span className={valueClass}>{dateToAge(petDetails?.dob)},</span>
                  )}

                  {petDetails?.weight === null ? (
                    <span className="text-xs font-normal text-[#ff0000]">Weight !</span>
                  ) : (
                    <span className={valueClass}>{petDetails?.weight} kgs</span>
                  )}
                </div>
              </div>

              <RBACFlag actionId="EDIT_PET">
                <button
                  onClick={handleEditPet}
                  className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-neutral-100"
                >
                  <ImPencil className="h-3 w-3" />
                </button>
              </RBACFlag>

              {petDetails?.high_risk_patient && (
                <div className="rounded-2xl bg-[#fff5f6] px-4 py-1.5 text-xs font-medium text-[#d92500]">
                  {petDetails?.high_risk_patient}
                </div>
              )}
            </div>
            <div className="">
              <p className={labelClass}>Gender:</p>
              <div className="flex flex-wrap items-center gap-1 text-xs">
                {petDetails?.gender === null ? (
                  <p className="text-xs font-normal text-[#ff0000]">No Gender</p>
                ) : (
                  <p className="text-xs font-normal">{petDetails?.gender}</p>
                )}
              </div>
            </div>
            <div className="">
              <p className={labelClass}>Pet Type:</p>
              <PetSpecies variant="DEFAULT" petSpeciesId={petDetails?.pet_species_type_id} />
            </div>
            <div className="">
              <p className={labelClass}>Pet Breed:</p>
              {petDetails?.pet_species_type_id === null ? (
                <p className="text-xs font-normal text-[#ff0000]">Breed !,</p>
              ) : (
                <PetBreed petBreedId={petDetails?.pet_breed_id} petSpeciesId={petDetails?.pet_species_type_id} />
              )}
            </div>

            <div className="">
              <p className={labelClass}>Vaccination:</p>
              <PetVaccinationStatus status={petDetails?.vaccination_status} type="vaccination" />
            </div>

            <div className="">
              <p className={labelClass}>Deworming:</p>
              <PetVaccinationStatus status={petDetails?.deworming_status} type="deworming" />
            </div>

            <div className="">
              <p className={labelClass}>Status:</p>
              <div className="flex items-center gap-2">
                {petDetails?.status?.toLowerCase() === "active" && (
                  <div className="h-3 w-3 rounded-full bg-[#149463]" />
                )}
                {petDetails?.status?.toLowerCase() === "inactive" && (
                  <div className="h-3 w-3 rounded-full bg-blue-700" />
                )}
                {petDetails?.status?.toLowerCase() === "deceased" && (
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                )}
                <span className="text-xs font-normal text-black">
                  {petDetails?.status?.toLowerCase() === "active" && "Active"}
                  {petDetails?.status?.toLowerCase() === "inactive" && "In Active"}
                  {petDetails?.status?.toLowerCase() === "deceased" && "Deceased"}
                </span>
              </div>
            </div>

          </div >
        ) : !isOrdersRoute ? (
          <div className="flex w-full flex-wrap items-center justify-between gap-3 py-2">
            <p className="text-theme-sm text-[#808DA0]">No pet found for this customer.</p>
            <div className="flex flex-wrap items-center gap-2">
              <RBACFlag actionId="ADD_PET">
                <button
                  onClick={handleAddPet}
                  className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-theme-sm font-medium text-white"
                >
                  + Add Pet
                </button>
              </RBACFlag>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-1 rounded-md border border-transparent bg-black px-4 py-2 text-theme-sm font-medium text-white"
              >
                <IoRefresh className="h-4 w-4" /> Refresh
              </button>
            </div>
          </div>
        ) : null}
    </>
  );
};

export default PetInformationDefaultView;
